/**
 * BuddyGroupsPage — Master-detail layout for buddy groups.
 *
 * Left panel: group list with pending invitations and create button.
 * Right panel: selected group detail with members, edit, transfer, delete.
 * Both /my/buddy-groups and /my/buddy-groups/:groupId render this page.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Users,
  Plus,
  Crown,
  ArrowLeft,
  Trash2,
  Loader2,
  AlertTriangle,
  LogOut,
  Calendar,
  MapPin,
  Lock,
  Pencil,
} from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { SectionLabel } from '@/components/ui/section-label';
import { formatDateRange } from '@/utils/formatters';
import { formatPreferenceParts } from '@/utils/format';
import { formatInTimezone } from '@/utils/timezone';
import { useBuddyGroupRodeos } from '@/hooks/useBuddyGroupRodeos';
import { useBuddyGroupPreferencesEditable } from '@/hooks/useBuddyGroupPreferences';
import type { BuddyGroupRodeoEntry } from '@/types/buddy-groups';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { BuddyGroupCard } from '@/components/buddy-groups/BuddyGroupCard';
import { CreateBuddyGroupModal } from '@/components/buddy-groups/CreateBuddyGroupModal';
import { PendingInvitationsList } from '@/components/buddy-groups/PendingInvitationsList';
import { BuddyGroupEditForm } from '@/components/buddy-groups/BuddyGroupEditForm';
import { BuddyGroupManageMembers } from '@/components/buddy-groups/BuddyGroupManageMembers';
import { TransferPrimaryModal } from '@/components/buddy-groups/TransferPrimaryModal';
import { EditBuddyGroupPreferencesDialog } from '@/components/buddy-groups/EditBuddyGroupPreferencesDialog';
import { useBuddyGroups } from '@/hooks/useBuddyGroups';
import { useBuddyGroup } from '@/hooks/useBuddyGroup';
import { useBuddyGroupMutations } from '@/hooks/useBuddyGroupMembers';
import {
  usePendingInvitations,
  useAcceptInvitation,
  useDeclineInvitation,
} from '@/hooks/useBuddyGroupInvitations';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import { useToast } from '@/contexts/ToastContext';
import { useNotifications } from '@/hooks/useNotifications';
import { useBreadcrumb } from '@/contexts/BreadcrumbContext';
import { isFullProfileComplete } from '@/utils/profileCompleteness';

const statusVariant: Record<string, string> = {
  active: 'bg-green-500/15 text-green-600 dark:text-green-400',
  pending: 'bg-yellow-500/15 text-yellow-600 dark:text-yellow-400',
  locked: 'bg-blue-500/15 text-blue-600 dark:text-blue-400',
  disbanded: 'bg-zinc-500/15 text-zinc-500',
};

export default function BuddyGroupsPage() {
  const { groupId } = useParams<{ groupId: string }>();
  const navigate = useNavigate();
  const { data: groups, isLoading, error } = useBuddyGroups();
  const [createOpen, setCreateOpen] = useState(false);

  // Invitation hooks
  const { profile } = useAuthStore();
  const userProfileId = profile?.id ?? '';
  const {
    data: pendingInvitations,
    isLoading: invitationsLoading,
  } = usePendingInvitations(userProfileId);
  const acceptMutation = useAcceptInvitation();
  const declineMutation = useDeclineInvitation();
  const { showSuccess, showError } = useToast();
  const { refresh: refreshNotifications } = useNotifications();

  const [processingInvitationId, setProcessingInvitationId] = useState<
    string | null
  >(null);

  const handleAccept = async (invitationId: string) => {
    // Inviting someone is intentionally NOT gated on the inviter's
    // profile completeness — only acceptance is. This keeps the
    // sender's flow friction-free and pushes the data-collection
    // requirement onto the user who's about to enter events through
    // the group. On save, ProfileInfoTab honors the `returnTo`
    // query param and bounces them back here to retry.
    if (!isFullProfileComplete(profile as Record<string, unknown> | null)) {
      showError(
        'Complete your profile first',
        'You need a full profile before you can accept group invitations.',
      );
      navigate(`/profile?returnTo=${encodeURIComponent('/my/buddy-groups')}`);
      return;
    }
    setProcessingInvitationId(invitationId);
    try {
      await acceptMutation.mutateAsync(invitationId);
      showSuccess('Invitation accepted! You have joined the group.');
      refreshNotifications();
    } catch (err: any) {
      showError(err?.message ?? 'Failed to accept invitation');
    } finally {
      setProcessingInvitationId(null);
    }
  };

  const handleDecline = async (invitationId: string) => {
    setProcessingInvitationId(invitationId);
    try {
      await declineMutation.mutateAsync(invitationId);
      showSuccess('Invitation declined.');
      refreshNotifications();
    } catch (err: any) {
      showError(err?.message ?? 'Failed to decline invitation');
    } finally {
      setProcessingInvitationId(null);
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users className="h-6 w-6 text-primary" />
          <h1 className="text-xl font-bold">My Buddy Groups</h1>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-1.5" />
          Create Group
        </Button>
      </div>

      {/* Pending invitations — full width above the split */}
      <PendingInvitationsList
        invitations={pendingInvitations ?? []}
        onAccept={handleAccept}
        onDecline={handleDecline}
        isLoading={invitationsLoading}
        processingId={processingInvitationId}
      />

      {/* Master-detail split — side-by-side on md+, stacked on mobile */}
      <div className="flex flex-col md:flex-row gap-4 items-start min-h-[400px]">
        {/* Left panel — group list (hidden on mobile when a group is selected) */}
        <div className={`md:w-72 md:shrink-0 space-y-2 ${groupId ? 'hidden md:block' : ''}`}>
          {/* Loading */}
          {isLoading && (
            <div className="space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-16 rounded-lg" />
              ))}
            </div>
          )}

          {/* Error */}
          {error && !isLoading && (
            <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
              Failed to load buddy groups.
            </div>
          )}

          {/* Empty state */}
          {!isLoading && !error && (!groups || groups.length === 0) && (
            <div className="text-center py-8">
              <Users className="h-8 w-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No groups yet</p>
              <Button
                size="sm"
                variant="outline"
                className="mt-3"
                onClick={() => setCreateOpen(true)}
              >
                Create Your First Group
              </Button>
            </div>
          )}

          {/* Group list items */}
          {!isLoading && groups && groups.length > 0 && (
            <div className="space-y-1">
              {[...groups].sort((a, b) =>
                (a.group_name ?? '').localeCompare(b.group_name ?? '')
              ).map((group) => (
                <BuddyGroupCard
                  key={group.id}
                  group={group}
                  isSelected={group.id === groupId}
                  onClick={() => navigate(`/my/buddy-groups/${group.id}`)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right panel — detail */}
        <div className={`flex-1 min-w-0 ${!groupId ? 'hidden md:block' : ''}`}>
          {groupId ? (
            <>
              {/* Mobile back button */}
              <button
                type="button"
                onClick={() => navigate('/my/buddy-groups')}
                className="md:hidden inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground mb-3"
              >
                <ArrowLeft className="h-4 w-4" />
                Back to groups
              </button>
              <BuddyGroupDetailPanel
                groupId={groupId}
                currentUserProfileId={userProfileId}
                onDeleted={() => navigate('/my/buddy-groups')}
              />
            </>
          ) : (
            <div className="flex items-center justify-center h-full rounded-lg border border-dashed">
              <div className="text-center py-12">
                <Users className="h-10 w-10 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  Select a group to view details
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Create modal */}
      <CreateBuddyGroupModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        existingNames={(groups ?? []).map((g) => g.group_name).filter(Boolean) as string[]}
      />
    </div>
  );
}

// ─── Detail panel (right side) ──────────────────────────────────────────────

function BuddyGroupDetailPanel({
  groupId,
  currentUserProfileId,
  onDeleted,
}: {
  groupId: string;
  currentUserProfileId: string;
  onDeleted: () => void;
}) {
  const { data: group, isLoading, error } = useBuddyGroup(groupId);
  const { renameGroup, deleteGroup, leaveGroup } = useBuddyGroupMutations();
  const { showSuccess, showError } = useToast();
  const { setOverride } = useBreadcrumb();
  const [showTransfer, setShowTransfer] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);

  // Set breadcrumb override so the UUID segment shows the group name
  useEffect(() => {
    if (group?.group_name) {
      setOverride(groupId, group.group_name);
    }
  }, [groupId, group?.group_name, setOverride]);

  if (isLoading) {
    return (
      <div className="space-y-4 p-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-48 rounded-lg" />
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4 text-sm text-destructive">
        {error ? 'Failed to load group details.' : 'Group not found.'}
      </div>
    );
  }

  const isPrimaryUser =
    group.primary_member_user_profile_id === currentUserProfileId;
  // Locked = draw started; truly frozen.
  // Disbanded = inactive but recoverable: the primary can still invite
  // members back (auto-revives the group when min size is met) or delete
  // the group entirely. Other manage actions (rename, transfer primary,
  // remove members) stay disabled until revived.
  const isLocked = group.status === 'locked';
  const isDisbanded = group.status === 'disbanded';
  const isManageBlocked = isLocked || isDisbanded;

  const handleRename = async (newName: string) => {
    await renameGroup.mutateAsync({ groupId: group.id, groupName: newName });
  };

  const handleDelete = async () => {
    try {
      await deleteGroup.mutateAsync(group.id);
      showSuccess('Buddy group deleted.');
      setShowDeleteConfirm(false);
      onDeleted();
    } catch (err: any) {
      showError(err?.message ?? 'Failed to delete group');
    }
  };

  const handleLeave = async () => {
    try {
      await leaveGroup.mutateAsync(group.id);
      showSuccess('You have left the buddy group.');
      setShowLeaveConfirm(false);
      onDeleted();
    } catch (err: any) {
      showError(err?.message ?? 'Failed to leave group');
    }
  };

  return (
    // Violet border-2 + left accent stripe matches the buddy-group
    // palette used across the dashboard, Event Detail, Rodeo Detail,
    // and BuddyGroupSetup so this surface reads as the same concept.
    <div className="relative rounded-lg border-2 border-violet-300 dark:border-violet-800 bg-card p-5 pl-6 space-y-5 overflow-hidden">
      <span
        aria-hidden
        className="absolute left-0 top-0 bottom-0 w-1 bg-violet-500/70"
      />
      {/* Header */}
      <div className="space-y-2">
        <div className="flex items-center gap-3">
          <BuddyGroupEditForm
            currentName={group.group_name}
            onSave={handleRename}
            disabled={!isPrimaryUser || isManageBlocked}
          />
          <Badge
            variant="outline"
            className={`text-xs ${statusVariant[group.status] ?? statusVariant.pending}`}
          >
            {group.status}
          </Badge>
        </div>

        {isPrimaryUser && (
          <p className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
            <Crown className="h-3 w-3" />
            You are the primary cardholder
          </p>
        )}
      </div>

      {/* Disbanded notice — primaries can still invite members to revive
          the group, or delete it entirely. */}
      {isDisbanded && isPrimaryUser && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          This group is disbanded because it dropped below the minimum
          member count. Invite members below — once at least one accepts,
          the group reactivates automatically. You can also delete it
          permanently.
        </div>
      )}

      {/* Members section. Invites are still allowed on disbanded groups
          (the only path back to active); other manage actions stay
          gated by isLocked-only checks below. */}
      <div className="space-y-2">
        <SectionLabel
          icon={<Users className="h-3.5 w-3.5" />}
          color="violet"
          trailing={
            <Badge variant="outline" className="text-[10px]">
              {group.members.length}
            </Badge>
          }
        >
          Members
        </SectionLabel>
        <div className="rounded-lg border p-4">
          <BuddyGroupManageMembers
            groupId={group.id}
            rodeoId={group.rodeo_id ?? undefined}
            members={group.members}
            currentUserProfileId={currentUserProfileId}
            isPrimaryUser={isPrimaryUser && !isLocked}
          />
        </div>
      </div>

      {/* Upcoming rodeos — surface every rodeo this group is registered
          for so members can see dates, location, and the preferences the
          primary locked in for that rodeo. The primary also gets an Edit
          affordance per upcoming rodeo (until that rodeo's draw runs). */}
      <BuddyGroupUpcomingRodeos
        groupId={group.id}
        canEditPreferences={isPrimaryUser && !isDisbanded}
      />

      {/* Actions */}
      {!isLocked && (
        <div className="flex flex-wrap gap-2">
          {isPrimaryUser && (
            <>
              {!isDisbanded && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowTransfer(true)}
                  disabled={group.members.filter((m) => m.invitation_status !== 'pending').length < 2}
                >
                  <Crown className="h-3.5 w-3.5 mr-1.5" />
                  Transfer Primary
                </Button>
              )}
              <Button
                size="sm"
                variant="outline"
                className="text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={() => setShowDeleteConfirm(true)}
              >
                <Trash2 className="h-3.5 w-3.5 mr-1.5" />
                Delete Group
              </Button>
            </>
          )}
          {!isPrimaryUser && !isDisbanded && (
            <Button
              size="sm"
              variant="outline"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
              onClick={() => setShowLeaveConfirm(true)}
            >
              <LogOut className="h-3.5 w-3.5 mr-1.5" />
              Leave Group
            </Button>
          )}
        </div>
      )}

      {/* Transfer modal */}
      <TransferPrimaryModal
        open={showTransfer}
        onOpenChange={setShowTransfer}
        groupId={group.id}
        members={group.members}
        currentUserProfileId={currentUserProfileId}
      />

      {/* Delete confirmation modal */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <Trash2 className="h-5 w-5" />
              Delete Buddy Group
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div className="flex items-start gap-2 rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <p>
                This will permanently delete <strong>{group.group_name ?? 'this group'}</strong> and
                remove all {group.members.length} member{group.members.length !== 1 ? 's' : ''}.
                This action cannot be undone.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(false)}
              disabled={deleteGroup.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteGroup.isPending}
            >
              {deleteGroup.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              Delete Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Leave confirmation modal */}
      <Dialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogOut className="h-5 w-5" />
              Leave Buddy Group
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <p className="text-sm text-muted-foreground">
              Are you sure you want to leave <strong>{group.group_name ?? 'this group'}</strong>?
              All members will be notified.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLeaveConfirm(false)}
              disabled={leaveGroup.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleLeave}
              disabled={leaveGroup.isPending}
            >
              {leaveGroup.isPending ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <LogOut className="h-4 w-4 mr-2" />
              )}
              Leave Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─── Upcoming rodeos section ─────────────────────────────────────────

function BuddyGroupUpcomingRodeos({
  groupId,
  canEditPreferences,
}: {
  groupId: string;
  /** True when the viewer is the primary cardholder of an editable group. */
  canEditPreferences: boolean;
}) {
  const { data: rodeos, isLoading, error } = useBuddyGroupRodeos(groupId);

  const upcoming = (rodeos ?? []).filter((r) => r.is_upcoming);
  const past = (rodeos ?? []).filter((r) => !r.is_upcoming);

  return (
    <div className="space-y-2">
      <SectionLabel
        icon={<Calendar className="h-3.5 w-3.5" />}
        color="violet"
        trailing={
          <Badge variant="outline" className="text-[10px]">
            {upcoming.length}
          </Badge>
        }
      >
        Upcoming Rodeos
      </SectionLabel>

      {isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-20 rounded-lg" />
          <Skeleton className="h-20 rounded-lg" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-3 text-xs text-destructive">
          Failed to load rodeos for this group.
        </div>
      ) : upcoming.length === 0 ? (
        <div className="rounded-lg border border-dashed p-4 text-center text-xs text-muted-foreground">
          This group isn&apos;t registered for any upcoming rodeos.
        </div>
      ) : (
        <div className="space-y-2">
          {upcoming.map((rodeo) => (
            <BuddyGroupRodeoRow
              key={rodeo.rodeo_id}
              rodeo={rodeo}
              groupId={groupId}
              canEditPreferences={canEditPreferences}
            />
          ))}
        </div>
      )}

      {past.length > 0 && (
        <details className="mt-2 rounded-lg border bg-muted/30">
          <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground">
            Past rodeos ({past.length})
          </summary>
          <div className="space-y-2 px-3 pb-3 pt-1">
            {past.map((rodeo) => (
              <BuddyGroupRodeoRow
                key={rodeo.rodeo_id}
                rodeo={rodeo}
                groupId={groupId}
                // Past rodeos are history — preferences never change there.
                canEditPreferences={false}
                muted
              />
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

function BuddyGroupRodeoRow({
  rodeo,
  groupId,
  canEditPreferences,
  muted,
}: {
  rodeo: BuddyGroupRodeoEntry;
  groupId: string;
  canEditPreferences: boolean;
  muted?: boolean;
}) {
  const dateRange = formatDateRange(rodeo.start_date, rodeo.end_date);
  const hasPrefs = !!rodeo.preferences_first || !!rodeo.preferences_second;
  const [editOpen, setEditOpen] = useState(false);

  // Only asked when the viewer could actually act on the answer. The
  // backend re-checks on save, so this gate is a courtesy, not the
  // authority — a draw that runs while the page is open surfaces as an
  // error in the dialog rather than a silently ignored save.
  const { data: editable } = useBuddyGroupPreferencesEditable(
    groupId,
    rodeo.rodeo_id,
    canEditPreferences,
  );
  const frozen = editable ? !editable.allowed : false;
  // Finals rodeos run every contestant in every performance; the backend
  // rejects setPreferences there with 409, so don't offer the edit at all.
  const showEdit = canEditPreferences && !frozen && rodeo.is_finals !== true;

  return (
    <div
      className={`rounded-lg border p-3 ${
        muted
          ? 'border-foreground/10 bg-card/50'
          : 'border-violet-200 bg-violet-50/40 dark:border-violet-900/60 dark:bg-violet-950/20'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold truncate">
              {rodeo.name ?? 'Unnamed rodeo'}
            </span>
            {rodeo.rodeo_or_jackpot === 'jackpot' && (
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4">
                Jackpot
              </Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
            {dateRange && (
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-3 w-3" />
                {dateRange}
              </span>
            )}
            {rodeo.location && (
              <span className="inline-flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {rodeo.location}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Locked-in preferences for THIS (group, rodeo) attachment.
          preferences_locked_at is null when the primary hasn't committed
          yet — surface that explicitly so members know the values aren't
          frozen. Finals rodeos have no preferences at all. */}
      {rodeo.is_finals === true ? (
        <p className="mt-3 rounded-md border border-foreground/10 bg-background/60 p-2.5 text-xs text-muted-foreground">
          Finals rodeo — every contestant runs in every performance, so there
          are no performance preferences to set.
        </p>
      ) : (
      <div className="mt-3 rounded-md border border-foreground/10 bg-background/60 p-2.5">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
            <Lock className="h-3 w-3" />
            {rodeo.preferences_locked_at
              ? 'Locked preferences'
              : 'Preferences (not yet locked)'}
          </div>
          {showEdit && (
            <Button
              size="sm"
              variant="ghost"
              className="h-6 px-2 text-[11px] text-violet-700 hover:text-violet-800 dark:text-violet-300"
              onClick={() => setEditOpen(true)}
            >
              <Pencil className="h-3 w-3 mr-1" />
              {hasPrefs ? 'Edit' : 'Set preferences'}
            </Button>
          )}
          {canEditPreferences && frozen && (
            <span className="text-[11px] text-muted-foreground">
              Draw has run — preferences are frozen
            </span>
          )}
        </div>
        {hasPrefs ? (
          <dl className="mt-1.5 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
            <PreferenceCell
              label="1st"
              value={rodeo.preferences_first}
              timezone={rodeo.timezone}
            />
            <PreferenceCell
              label="2nd"
              value={rodeo.preferences_second}
              timezone={rodeo.timezone}
            />
          </dl>
        ) : (
          <p className="mt-1 text-xs text-muted-foreground">
            {showEdit
              ? 'No performance preferences set for this rodeo yet — set them for the whole group.'
              : 'The primary cardholder hasn’t set performance preferences for this rodeo yet.'}
          </p>
        )}
      </div>
      )}

      {/* Stay mounted while open even if the editable check flips to
          frozen underneath us — the user gets the server's rejection on
          save instead of the dialog vanishing mid-edit. */}
      {(showEdit || editOpen) && (
        <EditBuddyGroupPreferencesDialog
          open={editOpen}
          onClose={() => setEditOpen(false)}
          groupId={groupId}
          rodeoId={rodeo.rodeo_id}
          rodeoName={rodeo.name}
          timezone={rodeo.timezone}
          currentFirst={rodeo.preferences_first}
          currentSecond={rodeo.preferences_second}
        />
      )}
    </div>
  );
}

/**
 * Render a single preference (1st / 2nd) with a friendly format. The
 * stored value is `<title> [<isoDatetime>]` for performance picks, or
 * a bare label like "Slack" / "Draw Out" for non-performance options.
 * When an ISO datetime is present we render it in the rodeo's saved
 * timezone with the abbreviation appended (e.g. "Mon, Jun 22, 7:00 PM
 * PT") so every viewer sees the time the admin entered, regardless of
 * their browser locale.
 */
function PreferenceCell({
  label,
  value,
  timezone,
}: {
  label: string;
  value: string | null;
  timezone: string | null;
}) {
  if (!value) {
    return (
      <div className="flex items-baseline gap-1.5">
        <dt className="text-muted-foreground">{label}:</dt>
        <dd className="font-medium">—</dd>
      </div>
    );
  }
  // Pass the rodeo's saved timezone so the helper anchors any datetime
  // suffix to that zone and appends the US abbreviation ("Fri, May 15,
  // 6:00 PM PT"). Legacy values like "Slack" / "Draw Out" round-trip
  // unchanged because they have no datetime portion.
  const parts = formatPreferenceParts(value, timezone);
  const isoMatch = value.trim().match(/\[([^\]]+)\]\s*$/);
  const tzDatetime =
    isoMatch && timezone
      ? formatInTimezone(
          isoMatch[1],
          timezone,
          {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          },
          true,
        )
      : parts.datetime;

  return (
    <div className="min-w-0">
      <div className="flex items-baseline gap-1.5">
        <dt className="text-muted-foreground">{label}:</dt>
        <dd className="font-medium truncate">{parts.title || value}</dd>
      </div>
      {tzDatetime && (
        <div className="mt-0.5 ml-5 text-[11px] text-muted-foreground">
          {tzDatetime}
        </div>
      )}
    </div>
  );
}
